import React from 'react';
import { useTranslation } from 'react-i18next';

const CheckIcon = ({ size = 14, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

interface PersonaI18n {
  name: string;
  role: string;
  headline: string;
  bullets: string[];
  modules: string[];
}

// Visual config (avatar URL + role badge color) stays in code;
// strings come from i18n.
const PERSONA_VISUALS = [
  { avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&h=200&fit=crop&crop=faces', roleColor: 'bg-black text-white' },
  { avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=faces', roleColor: 'bg-gray-900 text-white' },
  { avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&crop=faces', roleColor: 'bg-gray-800 text-white' },
];

export const PersonasSection = React.memo(() => {
  const { t } = useTranslation();
  const items = t('landing.personas.items', { returnObjects: true }) as PersonaI18n[];

  return (
    <section className="py-24 px-6 bg-gray-50/60 border-y border-gray-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">{t('landing.personas.eyebrow')}</span>
          <h2
            className="text-3xl md:text-5xl font-medium mt-3 mb-5"
            style={{
              background: 'linear-gradient(to bottom, #000000, #000000, rgba(0, 0, 0, 0.6))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.04em',
            }}
          >
            {t('landing.personas.titleLine1')}
            <br />
            {t('landing.personas.titleLine2')}
          </h2>
          <p className="text-base max-w-2xl mx-auto text-gray-500">{t('landing.personas.subtitle')}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((p, i) => {
            const visual = PERSONA_VISUALS[i];
            return (
              <div key={p.name} className="bg-white border border-gray-200 rounded-2xl p-7 hover:shadow-lg hover:shadow-black/5 hover:border-gray-300 transition-all">
                <div className="flex items-center gap-4 mb-5">
                  <img src={visual.avatar} alt={p.name} className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm" loading="lazy" />
                  <div>
                    <div className="text-base font-semibold text-black">{p.name}</div>
                    <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full mt-1 ${visual.roleColor}`}>{p.role}</span>
                  </div>
                </div>
                <h3 className="text-base font-semibold mb-4 text-black leading-snug">&ldquo;{p.headline}&rdquo;</h3>
                <ul className="space-y-2.5 mb-6">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckIcon size={14} className="text-black mt-0.5 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="pt-5 border-t border-gray-100">
                  <div className="text-[10px] font-semibold uppercase tracking-wider mb-2 text-gray-400">{t('landing.personas.modulesLabel')}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {p.modules.map((m) => (
                      <span key={m} className="text-[11px] px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200">{m}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
});
PersonasSection.displayName = 'PersonasSection';
